/**
 * Local timezone-aware date utility functions.
 * Prevents timezone shift bugs caused by Date.prototype.toISOString().
 */

/**
 * Formats a Date object as 'YYYY-MM-DD' in the user's LOCAL timezone.
 */
export const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a Date object as 'YYYY-MM' in the user's LOCAL timezone.
 */
export const getLocalMonthString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Safely parses a 'YYYY-MM-DD' string into a local Date object.
 */
export const parseLocalDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const parts = dateStr.split('-');
  if (parts.length !== 3) return new Date();
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return new Date();
  return new Date(year, month, day);
};

/**
 * Shifts a 'YYYY-MM-DD' date string by a given number of days (+ or -) in local time.
 */
export const shiftDays = (dateStr: string, days: number): string => {
  const date = parseLocalDate(dateStr);
  date.setDate(date.getDate() + days);
  return getLocalDateString(date);
};

/**
 * Formats a 'YYYY-MM-DD' string into a localized display date string.
 */
export const formatDisplayDate = (
  dateStr: string,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString(
    locale,
    options || {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );
};
